import React from 'react';
import { useTranslation } from 'react-i18next';

export default function AuditTable({
  rows,
  columns,
  loading,
  emptyKey = 'content:noResults',
  onRowClick,
  limit,
  offset,
  onNext,
  onPrev,
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded border border-border-subtle">
      <div className="overflow-auto max-h-[60vh]">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-raised/80 sticky top-0">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="text-left px-3 py-2 text-label tracking-widest text-ink-secondary uppercase font-semibold border-b border-border-subtle"
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-ink-muted">···</td></tr>
            )}
            {!loading && (!rows || rows.length === 0) && (
              <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-ink-muted">{t(emptyKey)}</td></tr>
            )}
            {!loading && rows && rows.map((row, i) => (
              <tr
                key={row.event_id || row.id || row.run_id || i}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-border-subtle ${onRowClick ? 'cursor-pointer hover:bg-surface-raised/70' : ''}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 align-top text-ink-secondary">
                    {c.render ? c.render(row) : renderCell(row[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(onNext || onPrev) && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-border-subtle text-label tracking-widest text-ink-muted">
          <span>offset {offset ?? 0} · limit {limit ?? 50}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onPrev}
              disabled={!offset}
              className="px-3 py-1 border border-border-strong rounded disabled:opacity-40 hover:text-accent-text"
            >
              ←
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!rows || rows.length < (limit ?? 50)}
              className="px-3 py-1 border border-border-strong rounded disabled:opacity-40 hover:text-accent-text"
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function renderCell(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return <code className="text-xs text-ink-secondary">{JSON.stringify(v).slice(0, 120)}</code>;
  const s = String(v);
  return s.length > 200 ? <span title={s}>{s.slice(0, 200)}…</span> : s;
}

export function statusPill(status) {
  const map = {
    ok: 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-900',
    error: 'bg-red-50 text-red-800 border-red-300 dark:bg-red-950/60 dark:text-red-200 dark:border-red-800',
    partial: 'bg-accent/10 text-accent-text border-accent/40',
  };
  const cls = map[status] || 'bg-surface-raised text-ink-secondary border-border-strong';
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded border ${cls}`}>
      {status || '—'}
    </span>
  );
}

export function monoShort(v, len = 10) {
  if (!v) return '—';
  const s = String(v);
  return <code className="text-xs text-ink-secondary" title={s}>{s.slice(0, len)}…</code>;
}

export function formatTs(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch (_) {
    return iso;
  }
}

export function pre(value, max = 300) {
  if (value === null || value === undefined) return '—';
  const s = String(value);
  return (
    <pre className="whitespace-pre-wrap break-words text-xs text-ink-secondary max-w-xl">
      {s.length > max ? s.slice(0, max) + '…' : s}
    </pre>
  );
}
